import React, { Component, createElement, createRef, type ComponentProps, type KeyboardEvent } from 'react';
import { NavContext, type NavContextShape } from '../../../util/context/NavContext';

import { injectIntl, type IntlShape } from 'react-intl';
import { setState } from 'lib/react';
import { debounce } from 'lodash-es';

// Algolia
import { type Hit as InstantSearchHit } from 'instantsearch.js';
import { SearchBox, Highlight, Snippet, useInstantSearch } from 'react-instantsearch';

// Components
import { Link, navigate } from 'gatsby';
import { InlineLoading } from '@carbon/react';
import { Close as IconClose, Search as IconSearch, Send as IconSend } from '@carbon/react/icons';

// CSS
import cn from 'clsx';
import * as css from './GlobalSearch.module.scss';

type Hit = InstantSearchHit<{
    title: string;
    description: null | string;
    content: string;

    // Attributes parsed from URL
    lang: string; // example: "en"
    prod: string; // example:     "braiins-pool"
    slug: string; // example:     "braiins-pool/about"
    path: string; // example: "/en/braiins-pool/about"
}>;
type SearchBoxProps = ComponentProps<typeof SearchBox>;

export interface GlobalSearchProps {
    className?: string;
    style?: CSSProperties;
}
interface Props extends GlobalSearchProps {
    intl: IntlShape;
    searchApi: ReturnType<typeof useInstantSearch>;
}

type State = {
    query: string;
    inputIsFocused: boolean;

    isManagingFocus: boolean;
    focusedItem: number;
};
const getInitialState = (query?: string): State => ({
    query: query || '',
    inputIsFocused: false,

    isManagingFocus: false,
    focusedItem: 0,
});

class View extends Component<Props, State> {
    private txt: Record<'search' | 'open' | 'submit' | 'reset', string>;
    constructor(props: Props) {
        super(props);
        this.state = getInitialState(props.searchApi.indexUiState.query);

        const { intl } = props;
        this.txt = {
            search: intl.formatMessage({ defaultMessage: 'Search' }),
            open: intl.formatMessage({ defaultMessage: 'Open search' }),
            submit: intl.formatMessage({ defaultMessage: 'Submit search' }),
            reset: intl.formatMessage({ defaultMessage: 'Reset search' }),
        };
    }

    static contextType = NavContext;
    private get ctx(): NavContextShape {
        return this.context as NavContextShape;
    }

    private openButtonRef = createRef<HTMLButtonElement>();
    private inputWrapperRef = createRef<HTMLDivElement>();
    private getInput = (): null | HTMLInputElement => {
        return this.inputWrapperRef.current?.querySelector<HTMLInputElement>('input[type="search"]') ?? null;
    };

    private open = () => {
        this.ctx.toggleNavState('searchIsOpen', 'open');
        setTimeout(this.focusInput, 100);
    };
    private close = () => this.ctx.toggleNavState('searchIsOpen', 'close');
    private focusInput = () => this.getInput()?.focus();
    private reset = async () => {
        await setState(this, getInitialState());
        this.close();

        const el = this.openButtonRef.current;
        if (el && this.state.isManagingFocus) el.focus();
    };

    private handleKeyDown = (e: KeyboardEvent) => {
        const { query, focusedItem } = this.state;
        const { results } = this.props.searchApi;

        switch (e.key) {
            case 'ArrowDown': {
                e.preventDefault();
                this.setState(s => ({
                    isManagingFocus: true,
                    focusedItem: (s.focusedItem + 1) % results.hits.length,
                }));
                break;
            }
            case 'ArrowUp': {
                e.preventDefault();
                this.setState(s => ({
                    isManagingFocus: true,
                    focusedItem: s.focusedItem - 1 < 0 ? results.hits.length - 1 : s.focusedItem - 1,
                }));
                break;
            }
            case 'Escape': {
                e.preventDefault();
                if (query === '') {
                    this.reset();
                } else {
                    this.setState({ query: '', isManagingFocus: true }, this.focusInput);
                }
                break;
            }
            case 'Enter': {
                e.preventDefault();
                const hit: Maybe<Hit> = results.hits[focusedItem];
                if (hit) this.navigate(hit);
                break;
            }
            default:
        }
    };
    private handleFocusCapture = e => {
        const target = e.target as HTMLElement;
        const { inputIsFocused } = this.state;

        const targetIsSearchInput = target instanceof HTMLInputElement;
        if (targetIsSearchInput && !inputIsFocused) this.setState({ inputIsFocused: true });
    };
    private handleBlurCapture = e => {
        const target = e.target as HTMLElement;
        const { inputIsFocused } = this.state;

        const targetIsSearchInput = target instanceof HTMLInputElement;
        if (targetIsSearchInput && inputIsFocused) this.setState({ inputIsFocused: false });
    };
    private handleFormRef = (el: null | HTMLFormElement) => {
        if (!el) return;
        el.addEventListener('reset', this.reset, { capture: true, once: true });
    };

    private getTargetURL = (hit: Hit, local: boolean): string => {
        const url = new URL(hit.path, location.origin);
        url.search = window.location.search;
        const href = url.href;

        return local ? href.replace(url.origin, '') : href;
    };
    private navigate = (hit: Hit): void => {
        navigate(this.getTargetURL(hit, true));
    };

    private handleQueryHook: SearchBoxProps['queryHook'] = debounce((query, hook) => {
        this.setState({ query }, () => hook(query));
    }, 500);
    private get ariaActiveDescendant(): string | undefined {
        const { hits } = this.props.searchApi.results;
        const { focusedItem } = this.state;

        if (hits.length > 0) return `menu-item-${focusedItem}`;
    }
    private get algTranslations(): SearchBoxProps['translations'] {
        return {
            submitButtonTitle: this.txt.submit,
        };
    }
    private get algClassNames(): SearchBoxProps['classNames'] {
        return {
            root: cn(css.algRoot, !this.ctx.searchIsOpen && css.hidden),
            input: cn(css.input, !this.ctx.searchIsOpen && css.hidden),
            submit: css.buttonSubmitAlgolia,
            reset: css.buttonResetAlgolia,
        };
    }
    private algSubmitIconComponent: SearchBoxProps['submitIconComponent'] = () => {
        return <IconSend size={20} />;
    };
    private algLoadingIconComponent: SearchBoxProps['loadingIconComponent'] = () => {
        return <InlineLoading status="active" />;
    };

    private renderInput(): ReactNode {
        const { intl } = this.props;
        const { query } = this.state;
        const { searchIsOpen } = this.ctx;

        return (
            <div
                className={css.inputWrapper}
                ref={this.inputWrapperRef}
                // Handlers
                onKeyDown={this.handleKeyDown}
                onFocus={this.handleFocusCapture}
                onBlur={this.handleBlurCapture}
            >
                <button
                    tabIndex={searchIsOpen ? -1 : 0}
                    className={cn(css.buttonSearchOur, searchIsOpen && css.inactive)}
                    ref={this.openButtonRef}
                    type="button"
                    aria-label={this.txt.open}
                    aria-expanded={searchIsOpen}
                    onClick={this.open}
                    children={<IconSearch size={20} />}
                />
                <SearchBox
                    // @ts-expect-error: The ref callback variant is indeed supported even though it is forbidden
                    formRef={this.handleFormRef}
                    searchAsYouType
                    classNames={this.algClassNames}
                    translations={this.algTranslations}
                    // Value
                    placeholder={this.txt.search}
                    queryHook={this.handleQueryHook}
                    defaultValue={query}
                    // Components
                    submitIconComponent={this.algSubmitIconComponent}
                    loadingIconComponent={this.algLoadingIconComponent}
                    // ARIA
                    tabIndex={searchIsOpen ? 0 : -1}
                    aria-autocomplete="list"
                    aria-controls="search-menu"
                    aria-activedescendant={this.ariaActiveDescendant}
                />
                {searchIsOpen ? (
                    <button
                        tabIndex={0}
                        className={css.buttonResetOur}
                        type="button"
                        aria-label={this.txt.reset}
                        onClick={this.reset}
                        children={<IconClose size={20} />}
                    />
                ) : null}
            </div>
        );
    }
    private renderMenu(): ReactNode {
        const { results } = this.props.searchApi;
        const { focusedItem, query } = this.state;
        const { searchIsOpen, getPathInfo } = this.ctx;
        const pathInfo = getPathInfo();

        let hits: Hit[] = results.hits;

        // If on a product page, re-sort the results
        // so that the current product is first
        if (pathInfo.prod) {
            const hitsLocal: Hit[] = [];
            const hitsOther: Hit[] = [];
            hits.forEach(hit => {
                if (hit.prod === pathInfo.prod) hitsLocal.push(hit);
                else hitsOther.push(hit);
            });
            hits = [...hitsLocal, ...hitsOther];
        }

        return (
            <ul
                aria-labelledby="search-label"
                role="menu"
                id="search-menu"
                className={cn(css.list, (!results.hits.length || !searchIsOpen || !query) && css.hidden)}
                onKeyDown={this.handleKeyDown}
                onScrollCapture={e => e.stopPropagation()}
                children={hits.map((hit: Hit, index: number) => {
                    return (
                        <li role="none" key={index}>
                            <Link
                                onClick={this.reset}
                                tabIndex={-1}
                                id={`menu-item-${index}`}
                                role="menuitem"
                                className={cn(css.link, focusedItem === index && css.active)}
                                to={this.getTargetURL(hit, true)}
                            >
                                <div className={css.linkTitle}>
                                    <Highlight<Hit> hit={hit} attribute="title" />
                                    <span className={css.linkProduct} children={hit.prod} />
                                </div>
                                {hit.description && (
                                    <Highlight<Hit> hit={hit} attribute="description" className={css.linkDescription} />
                                )}
                                <Snippet<Hit> hit={hit} attribute="content" className={css.linkSnippet} />
                            </Link>
                        </li>
                    );
                })}
            />
        );
    }

    render() {
        const { intl, className, style } = this.props;
        const { inputIsFocused } = this.state;
        const { searchIsOpen } = this.ctx;

        return (
            <div
                role="search"
                style={style}
                className={cn(
                    css.container,
                    !searchIsOpen && css.hidden,
                    inputIsFocused && css.inputFocusWithin,
                    className,
                )}
            >
                {this.renderInput()}
                {this.renderMenu()}
            </div>
        );
    }
}

const withIntl = injectIntl(View);
export function GlobalSearch(props: GlobalSearchProps) {
    const searchApi = useInstantSearch();
    return createElement(withIntl, { ...props, searchApi });
}
